import { useEffect, useState } from 'react';
import { Checkbox, TextInput } from '@pmi/dsm-react';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import { useFormikContext } from 'formik';
import { Form } from 'react-bootstrap';
import { RemoveSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import styles from './addressForm.module.scss';
import { useStatesQueryMutation } from '../../../../Hub/TrainingHub/EnrollmentCard/ProductModal/apiSlice';
import { Address } from '../../../models/ProfileModel';

export interface AddressFormProps {
  countries: { text: string; value: string }[];
  disabled: boolean;
  onLoading: (loading: boolean) => void;
  emptyAddress: boolean;
}

export default function AddressForm({
  countries,
  disabled,
  onLoading,
  emptyAddress,
}: AddressFormProps) {
  const formik = useFormikContext<Address>();
  const [statesQuery, { isLoading: statesLoading }] = useStatesQueryMutation();
  const countryRegionOptions: Option[] =
    countries?.map((c) => ({
      label: c.text,
      value: c.value,
    })) ?? [];

  const [countryOptions, setCountryOptions] = useState(countryRegionOptions);
  const [stateOptions, setStateOptions] = useState([]);
  const onCountryRegionChange = async (value: string) => {
    formik.setFieldTouched('state', true);
    if (value) {
      const statesResponse = await statesQuery({
        country: value,
      }).unwrap();
      const stateOptionsFromResponse = statesResponse?.map((opt) => ({
        label: opt.text,
        value: opt.value,
      }));
      setStateOptions(stateOptionsFromResponse);
    } else {
      formik.setFieldValue('stateProvince', null);
      setStateOptions([]);
    }
  };

  const handleCountryDropdownChange = async (e: any, value: string) => {
    formik.setFieldTouched('country', true);
    formik.setFieldValue('country', value);
    setCountryOptions(countryRegionOptions);
    await onCountryRegionChange(value);
  };

  const errorMsg = (msg: string) => (
    <div className="invalid-feedback">
      <div className="d-flex">
        <span className="d-block me-1">
          <RemoveSolidIcon fontSize="sm" />
        </span>
        <span className="d-block">{msg}</span>
      </div>
    </div>
  );

  useEffect(() => {
    onLoading(statesLoading);
  }, [statesLoading]);

  useEffect(() => {
    if (formik.values?.country?.length > 0) {
      onCountryRegionChange(formik.values?.country).then();
    }
  }, [formik.values?.country]);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 mb-3">
          <span className="text-danger">*</span> These fields are required.
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12 select-country" id="select-country">
          <Form.Group
            controlId="country"
            className={formik?.errors.country ? 'is-invalid' : null}
          >
            <Form.Label>
              Country/Region <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              aria-label="Country/Region"
              className={`dsm-input-field__form-control cursor-pointer ${
                formik.errors.country
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }`}
              name="country"
              onChange={(e) => handleCountryDropdownChange(e, e.target.value)}
              value={formik.values.country}
            >
              <option key="" value="" className="dsm-menu__label">
                Select
              </option>
              {countryOptions.map((option, i: number) => (
                <option
                  className="dsm-menu__label break-all-words mw-100"
                  key={`country-${option.value}-${i.toString()}`}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik.errors.country &&
              errorMsg('Please enter the Country / Region')}
          </Form.Group>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12">
          <Form.Group
            controlId="address1"
            className={formik.errors.address1 ? 'is-invalid' : null}
          >
            <Form.Label>
              Address Line 1 <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="address1"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.address1}
              className={
                formik.errors.address1 ? 'is-invalid' : styles.inputBorder
              }
              value={formik.values.address1}
              disabled={statesLoading || disabled}
            />
            {formik.errors.address1 &&
              errorMsg('Please enter the address line 1')}
          </Form.Group>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12">
          <Form.Group
            controlId="address2"
            className={formik.errors.address2 ? 'is-invalid' : null}
          >
            <Form.Label>Address Line 2</Form.Label>
            <TextInput
              name="address2"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.address2}
              className={
                formik.errors.address2 ? 'is-invalid' : styles.inputBorder
              }
              value={formik.values.address2}
              disabled={statesLoading || disabled}
            />
          </Form.Group>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12 mb-3">
          <Form.Group
            controlId="postalCode"
            className={formik.errors.postalCode ? 'is-invalid' : null}
          >
            <Form.Label>
              Postal Code <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="postalCode"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.postalCode}
              className={
                formik.errors.postalCode ? 'is-invalid' : styles.inputBorder
              }
              value={formik.values.postalCode}
              disabled={statesLoading || disabled}
            />
            {formik.errors.postalCode &&
              errorMsg('Please enter the Postal Code')}
          </Form.Group>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-6" data-testid="address_city_container_testid">
          <Form.Group
            controlId="city"
            className={formik.errors.city ? 'is-invalid' : null}
          >
            <Form.Label>
              City <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="city"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.city}
              className={formik.errors.city ? 'is-invalid' : styles.inputBorder}
              value={formik.values.city}
              disabled={statesLoading || disabled}
            />
            {formik.errors.city && errorMsg('Please enter the City')}
          </Form.Group>
        </div>
        <div className="col-6 select-state" id="select-state">
          <Form.Group
            controlId="state"
            className={`${formik.errors.state ? 'is-invalid' : null} ${
              formik.values?.country ? null : 'cursor-not-allowed'
            }`}
          >
            <Form.Label>
              State/Province <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              aria-label="State/Province"
              className={`dsm-input-field__form-control cursor-pointer ${
                formik.errors.state
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }
              ${formik.values?.country ? null : 'pointer-events-none'}`}
              name="state"
              onChange={(e) => {
                formik.setFieldValue('state', e.target.value);
              }}
              value={formik.values.state}
              disabled={statesLoading || disabled}
            >
              <option key="empty-state" value="" className="dsm-menu__label">
                Select
              </option>
              {stateOptions?.map((option) => (
                <option
                  className="dsm-menu__label"
                  key={`state-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik.errors.state &&
              errorMsg('Please enter the State / Province')}
          </Form.Group>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12 chk-primary-address" id="chk-primary-address">
          <Form.Group
            controlId="primary"
            className={formik.errors.postalCode ? 'is-invalid' : null}
          >
            <Checkbox
              name="primary"
              onChange={formik.handleChange}
              readOnly={emptyAddress}
              checked={formik.values.primary}
              label="Save as my primary billing address for future use"
              disabled={statesLoading || disabled}
            />
          </Form.Group>
        </div>
      </div>
    </>
  );
}
